import React, { useState, useCallback, useRef } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Button } from './ui/Buttons';
import { X, Upload, Crop } from 'lucide-react';
import { cn } from '../lib/utils';

interface AvatarEditorProps {
  currentAvatar: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (croppedImageBlob: Blob) => void;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0
): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Set canvas size to 400x400 (output size)
  const outputSize = 400;
  canvas.width = outputSize;
  canvas.height = outputSize;

  // pixelCrop is already in natural image coordinates
  // Draw the cropped portion of the image, scaled to output size
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize
  );

  // Create circular mask for the final output
  ctx.globalCompositeOperation = 'destination-in';
  ctx.beginPath();
  ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
  ctx.fill();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Canvas is empty'));
      }
    }, 'image/jpeg', 0.95);
  });
};

export const AvatarEditor: React.FC<AvatarEditorProps> = ({
  currentAvatar,
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [mode, setMode] = useState<'select' | 'crop'>('select');
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (PNG, JPG, or WEBP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result as string);
      setMode('crop');
    });
    reader.readAsDataURL(file);
  };

  const handleCropCurrent = () => {
    if (!currentAvatar) {
      setError('No current avatar to crop');
      return;
    }
    setImageSrc(currentAvatar);
    setMode('crop');
    setError('');
  };

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      setError('Please select and crop an image');
      return;
    }

    try {
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      onConfirm(croppedImageBlob);
      handleClose();
    } catch (err) {
      setError('Failed to process image. Please try again.');
      console.error('Error cropping image:', err);
    }
  };

  const handleClose = () => {
    setImageSrc('');
    setMode('select');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please select a valid image file (PNG, JPG, or WEBP)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setError('');
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result as string);
        setMode('crop');
      });
      reader.readAsDataURL(file);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-black text-foreground">Edit Profile Picture</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {mode === 'select' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                {currentAvatar && (
                  <button
                    onClick={handleCropCurrent}
                    className="group p-6 border-2 border-border rounded-xl hover:border-primary transition-all hover:bg-primary/5"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative">
                        <img
                          src={currentAvatar}
                          alt="Current avatar"
                          className="w-24 h-24 rounded-full object-cover ring-2 ring-border group-hover:ring-primary transition-all"
                        />
                        <div className="absolute inset-0 rounded-full bg-black/20 group-hover:bg-black/10 transition-all flex items-center justify-center">
                          <Crop size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      <span className="text-sm font-bold text-foreground">Crop Current</span>
                    </div>
                  </button>
                )}

                <button
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className="group p-6 border-2 border-dashed border-border rounded-xl hover:border-primary transition-all hover:bg-primary/5"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                      <Upload size={32} className="text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <span className="text-sm font-bold text-foreground">Upload New</span>
                    <span className="text-xs text-muted-foreground">PNG, JPG, WEBP • Max 5MB</span>
                  </div>
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {mode === 'crop' && imageSrc && (
            <div className="space-y-6">
              <div className="relative w-full h-96 bg-muted rounded-xl overflow-hidden">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: {
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                    },
                  }}
                />
              </div>

              <div className="space-y-3">
                <label className="text-xs font-black uppercase text-muted-foreground tracking-widest">
                  Zoom
                </label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
              </div>

              {croppedAreaPixels && (
                <div className="flex items-center justify-center p-4 bg-muted/50 rounded-xl border border-border">
                  <div className="relative">
                    <img
                      src={imageSrc}
                      alt="Preview"
                      className="w-20 h-20 rounded-full object-cover ring-2 ring-border"
                      style={{
                        clipPath: `circle(50% at 50% 50%)`,
                      }}
                    />
                  </div>
                  <div className="ml-4">
                    <p className="text-xs font-bold text-foreground">Preview</p>
                    <p className="text-[10px] text-muted-foreground">400×400px output</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive font-medium">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {mode === 'crop' && (
            <Button onClick={handleConfirm} disabled={!croppedAreaPixels}>
              Confirm
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
